import React from 'react';
import { formatPercentage, formatPrice, formatTickerToURLFriendly } from '@benzinga/utils';
import type { TrendingTopic } from '@benzinga/trending-topics-manager';
import styles from './Search.module.scss';
import { FallbackLogoSVG } from './FallbackLogoSVG';
import { Ticker } from '@benzinga/session';

export interface TrendingTicker extends Ticker {
  bzExchange?: string;
  close?: number;
  lastTradePrice?: number;
}

export interface TrendingSectionsProps {
  trendingTickers: TrendingTicker[];
  trendingTopics: TrendingTopic[];
  selectElementListClass: string;
  selectedIndex: number;
  loading?: boolean;
  onKeyDown: React.KeyboardEventHandler<HTMLAnchorElement>;
}

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.sectionTitle}>
    <span>{children}</span>
  </div>
);

const TrendingUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden
    className={className}
    fill="none"
    height={16}
    viewBox="0 0 16 16"
    width={16}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 11.5L6 7.5L9 10.5L14 5.5M14 5.5H10.5M14 5.5V9"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
    />
  </svg>
);

export const TrendingSections: React.FC<TrendingSectionsProps> = ({
  loading,
  onKeyDown,
  selectedIndex,
  selectElementListClass,
  trendingTickers,
  trendingTopics,
}) => {
  const activeClass = (index: number) => `${selectElementListClass}${index === selectedIndex ? ' active' : ''}`;

  if (trendingTickers.length === 0 && trendingTopics.length === 0) {
    if (!loading) return null;

    return (
      <div className="search-results-section">
        <SectionHeader>Trending</SectionHeader>
        <div className={`${styles.symbolPillList} search-results-wrapper`}>
          {Array.from({ length: 6 }, (_, index) => (
            <span aria-hidden className={styles.symbolPillSkeleton} key={index} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {trendingTickers.length > 0 && (
        <div className="search-results-section">
          <SectionHeader>Trending Tickers</SectionHeader>
          <div className={`${styles.symbolPillList} search-results-wrapper`}>
            {trendingTickers.map((ticker, idx) => {
              const price = ticker.lastTradePrice ?? ticker.price ?? ticker.close;
              const isUp = (ticker.changePercent ?? 0) > 0;
              return (
                <a
                  className={`${styles.symbolPill} ${activeClass(idx)}`}
                  href={ticker.symbol ? `/quote/${formatTickerToURLFriendly(ticker.symbol)}` : undefined}
                  key={ticker.symbol ?? ticker.name}
                  onKeyDown={onKeyDown}
                  tabIndex={-1}
                  title={ticker.name}
                >
                  {ticker.logo ? (
                    <span className={styles.symbolPillLogo} style={{ backgroundImage: `url(${ticker.logo})` }} />
                  ) : (
                    <FallbackLogoSVG letter={ticker.symbol?.charAt(0) ?? ''} shape="circle" size={22} />
                  )}
                  <span className={styles.symbolPillTitle}>{ticker.symbol}</span>
                  {price != null ? (
                    <>
                      <span className={styles.symbolPillPrice}>
                        ${formatPrice(price, { fixedPlace: 2, removeTrailingZero: true })}
                      </span>
                      {ticker.changePercent ? (
                        <span className={`${styles.symbolPillChange} ${isUp ? styles.positive : styles.negative}`}>
                          ({formatPercentage(ticker.changePercent)}%)
                        </span>
                      ) : null}
                    </>
                  ) : loading ? (
                    <span aria-hidden className={styles.symbolPillPriceSkeleton} />
                  ) : null}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {trendingTopics.length > 0 && (
        <div className="search-results-section">
          <SectionHeader>Trending Keywords</SectionHeader>
          <div className={`${styles.resultsWrapper} search-results-wrapper`}>
            {trendingTopics.map((topic, idx) => (
              <a
                className={`${styles.trendingKeywordItem} ${activeClass(idx + trendingTickers.length)}`}
                href={`/search?q=${encodeURIComponent(topic.name)}`}
                key={`${topic.taxonomy}-${topic.name}`}
                onKeyDown={onKeyDown}
                tabIndex={-1}
              >
                <TrendingUpIcon className={styles.trendingIcon} />
                <span>{topic.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
